import { Box, Link } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import AppError from "../AppError";
import {
  filterResourceBy,
  filtersFromSearchParams,
  updateFilter,
} from "../features/managed_plus_credits/utils/searchUtils";
import AccountsFilter from "../features/managed_plus_sox_emails/components/AccountsFilter";
import { SearchDataGrid } from "../features/managed_plus_sox_emails/components/SearchDataGrid";
import {
  NoEmailsFoundWarning,
  soxEmailDownload,
} from "../features/managed_plus_sox_emails/utils/managedPlusSoxEmailsUtils";
import useTitle from "../hooks/useTitle";

const DEFAULT_RESULT = { result: [], totalCount: 0 };

const DEFAULT_SORTING_MODEL = [{ field: "account_id", sort: "asc" }];

const AccountsSearchColumns = [
  {
    headerName: "Account ID",
    field: "account_id",
    flex: 1,
    minWidth: 100,
    maxWidth: 120,
    renderCell: ({ row }) => {
      return (
        <Link
          underline="hover"
          href={`/admin/managed_plus_accounts/${row.account_id}`}
        >
          {row.account_id}
        </Link>
      );
    },
  },
  { headerName: "Company Name", field: "company_name", flex: 1 },
  { headerName: "Account Email", field: "contact_email", flex: 1 },
];

const ManagedPlusSoxEmails = () => {
  useTitle("M+ SOX Emails");
  const [filters, setFilters] = useState(
    filtersFromSearchParams(DEFAULT_SORTING_MODEL)
  );
  const [result, setResult] = useState(DEFAULT_RESULT);
  const [isLoading, setIsLoading] = useState(true);
  const [noData, setNoData] = useState(false);

  const onDownloadActionClick = useCallback(async (params) => {
    const response = await soxEmailDownload({ id: params.row.id });
    window.open(response.download_url, "_blank");
  }, []);

  useEffect(() => {
    setIsLoading(true);
    filterResourceBy("sox_email", filters)
      .onSuccess(({ result, total_count: totalCount }) => {
        setResult({ result, totalCount });
        setNoData(result.length === 0);
      })
      .onError((error) => {
        setResult({
          ...DEFAULT_RESULT,
          error: "Failed to fetch SOX emails from server",
        });
      })
      .done(() => setIsLoading(false))
      .run();
  }, [filters]);

  if (result.error) {
    return <AppError error={result.error} />;
  }

  return (
    <>
      <div aria-label="Contacts list">
        <AccountsFilter
          filters={filters}
          onFilterChange={(updatedParams) =>
            setFilters((params) => updateFilter(params, updatedParams))
          }
        />
        <div style={{ height: 50 }} />
        <Box
          sx={{ height: 640, pt: 4, display: "flex", flexDirection: "column" }}
        >
          {!noData ? (
            <SearchDataGrid
              DataGridProps={{
                loading: isLoading,
              }}
              sortModel={filters.order}
              onSortChange={(model) =>
                setFilters((params) => ({ ...params, order: model }))
              }
              onPaginationChange={({ pageSize: perPage, page }) => {
                const currentPage = filters.perPage === perPage ? page : 0;
                setFilters((params) => ({
                  ...params,
                  perPage,
                  page: currentPage,
                }));
              }}
              searchResult={result}
              page={filters.page}
              pageSize={filters.perPage}
              columns={AccountsSearchColumns}
              onDownloadActionClick={onDownloadActionClick}
            />
          ) : (
            <NoEmailsFoundWarning />
          )}
        </Box>
      </div>
    </>
  );
};

export default ManagedPlusSoxEmails;
